




















































































import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { DatePicker as VDatePicker } from 'v-calendar';
import moment from 'moment';
import { mapState } from 'vuex';

@Component({
  components: {
    VDatePicker,
  },
  computed: mapState(['dashboard', 'partner']),
})
export default class DoubleDatePicker extends Vue {
  // number of days that can be displayed -
  // relies on window width
  @Prop({
    default: 20,
  })
  public readonly maxRangeLength!: number;

  @Prop() public readonly gtmContext!: string;

  // holds the current date range selected
  public dateRange: any = {
    start: moment().format('YYYY-MM-DD'),
    end: moment()
      .add(this.$store.getters.initialDaysToDisplay, 'd')
      .format('YYYY-MM-DD'),
  };

  // value of first datepicker
  public startDate: any = moment().format('YYYY-MM-DD');

  // value of last datepicker
  public tempDateRange: any = {
    start: moment().format('YYYY-MM-DD'),
    end: moment()
      .add(this.$store.getters.initialDaysToDisplay, 'd')
      .format('YYYY-MM-DD'),
  };

  public dashboard: any;
  public localeId: string = moment.locale();
  public endVisible = 'click';
  public updateComesFromLastDP = false;
  public updateComesFromFirstDP = false;
  public preventFirstInput = false;
  public preventEndInput = false;
  public preventEndDayClick = false;
  public isEditing = false;
  public firstOpen = false;
  public lastOpen = false;

  public datePickerTheme: any = {
    container: {
      light: 'calendar-container',
    },
    title: {
      light: 'calendar-title',
    },
    weekdays: {
      light: 'calendar-weekdays',
    },
    arrows: {
      light: 'calendar-arrows',
    },
    highlightStartEndClass: 'calendar-highlight-startend',
    bgAccentLow: {
      light: 'calendar-range-bg',
    },
    contentAccent: {
      light: 'calendar-content-accent',
    },
  };

  @Prop({
    default: () => {
      return {
        start: moment().format('YYYY-MM-DD'),
        end: moment()
          .add(2, 'y')
          .format('YYYY-MM-DD'),
      };
    },
  })
  public readonly availableDates: any;

  public get locale() {
    return {
      id: this.localeId,
      firstDayOfWeek: 2,
      masks: { weekdays: 'WW' },
    };
  }

  /**
   * - When a date range is selected, we allow the user to select any date in the next two years,
   * so that it's quick to navigate to any date
   * - When the user clicks on a date, either in the first or last datepicker, we only allow to select
   * the second date a few days past of after this date - so that the new range selected cannot be longer than
   * the max number of days we allow. We also don't allow the range to start in the past, or to end after two years
   * from today, as the API won't have data for these dates.
   */
  public get availableDatesEnd() {
    if (this.isEditing) {
      return {
        start: this.tempRangeLowerBound,
        end: this.tempRangeUpperBound,
      };
    }
    // A range is currently selected, we allow selection anywhere in the next two years
    return {
      start: moment().format('YYYY-MM-DD'),
      end: moment()
        .add(2, 'y')
        .format('YYYY-MM-DD'),
    };
  }

  /**
   * From a date, return date minus allowed max range length if it's after today ;
   * return today otherwise
   */
  public get tempRangeLowerBound() {
    if (
      moment(this.startDate)
        .subtract(this.maxRangeLength - 1, 'd')
        .isAfter(moment())
    ) {
      return moment(this.startDate)
        .subtract(this.maxRangeLength - 1, 'd')
        .format('YYYY-MM-DD');
    }
    return moment().format('YYYY-MM-DD');
  }

  /**
   * From a date, returns date plus allowed max range length if it's before two years from now;
   * return two years from now otherwise
   */
  public get tempRangeUpperBound() {
    if (
      moment(this.startDate)
        .add(this.maxRangeLength - 1, 'd')
        .isBefore(moment().add(2, 'years'))
    ) {
      return moment(this.startDate)
        .add(this.maxRangeLength - 1, 'd')
        .format('YYYY-MM-DD');
    }
    return moment()
      .add(2, 'years')
      .format('YYYY-MM-DD');
  }

  public get startDateAttributes() {
    return [
      {
        highlight: {
          color: 'orange',
        },
        dates: this.isEditing
          ? null
          : [
              {
                start: moment(this.dateRange.start, 'YYYY-MM-DD').toDate(),
                end: moment(this.dateRange.end, 'YYYY-MM-DD').toDate(),
              },
            ],
      },
    ];
  }

  public get startDateFormatted() {
    return this.firstOpen
      ? ''
      : moment(this.startDate, 'YYYY-MM-DD').format('DD/MM/YYYY');
  }

  public get endDateFormatted() {
    return this.lastOpen
      ? ''
      : moment(this.dateRange.end, 'YYYY-MM-DD').format('DD/MM/YYYY');
  }

  public handleLastHide() {
    this.lastOpen = false;
    this.preventEndDayClick = false;
    if (!this.tempDateRange || !this.tempDateRange.end) {
      if (this.isEditing && moment(this.startDate).isSameOrBefore(moment(this.dateRange.end))) {
        // User picked a new from date then closed the to-picker without selecting an end.
        // Commit the new start against the existing end instead of discarding the change.
        this.preventEndInput = true;
        this.updateComesFromLastDP = true;
        this.tempDateRange = { start: this.startDate, end: this.dateRange.end };
        this.dateRange = { start: this.startDate, end: this.dateRange.end };
        this.$nextTick(() => {
          this.updateComesFromLastDP = false;
        });
      } else {
        this.updateComesFromLastDP = true;
        this.startDate = this.dateRange.start;
        this.updateComesFromLastDP = false;
      }
      this.isEditing = false;
    }
  }

  public handleFirstHide() {
    this.firstOpen = false;
  }

  public handleFirstDPButtonClick() {
    this.firstOpen = false;
    this.lastOpen = false;
    this.isEditing = false;
    this.updateComesFromFirstDP = false;
    this.preventFirstInput = false;
    this.updateComesFromLastDP = false;
    this.firstOpen = true;
  }

  public handleLastDPButtonClick() {
    this.endVisible = 'click';
  }

  private firstDPFocusMonth() {
    this.lastOpen = false;
    this.firstOpen = true;
    const firstDP: any = this.$refs.firstDP;
    firstDP.$refs.calendar.move(this.dateRange.start);
  }

  private lastDPFocusMonth() {
    this.firstOpen = false;
    this.lastOpen = true;
    const lastDP: any = this.$refs.lastDP;
    lastDP.$refs.calendar.move(this.startDate);
  }

  private startInput(value: Date) {
    // do nothing further if clicking in the last datepicker triggers an input event in the first
    if (this.preventFirstInput) {
      this.preventFirstInput = false;
      return;
    }
    if (this.updateComesFromLastDP) {
      this.updateComesFromLastDP = false;
      return;
    }
    this.isEditing = true;
    this.updateComesFromFirstDP = true;
    this.endVisible = 'hidden';
    const lastDP: any = this.$refs.lastDP;
    this.$nextTick(() => {
      this.endVisible = 'visible';
      lastDP.updateValue({ start: value, end: null });
      lastDP.dragValue = {
        start: value,
        end: value,
      };
      if (lastDP.$refs.calendar) {
        lastDP.$refs.calendar.move(value);
      }
    });
    this.updateComesFromFirstDP = false;
  }

  private endDayClick(value: any) {

    if (value.isDisabled) {
      return;
    }
    const lastDP: any = this.$refs.lastDP;

    if (this.preventEndDayClick) {
      lastDP.hidePopover();
      this.preventEndDayClick = false;
      return;
    }

    if (this.isEditing) {
      // user is dragging
      this.preventEndInput = true;
      if (moment(this.startDate).isBefore(moment(value.date))) {
        lastDP.updateValue({ start: this.startDate, end: value.date });
        this.dateRange = { start: this.startDate, end: value.date };
      } else {
        lastDP.updateValue({ end: value.date, start: this.startDate });
        this.dateRange = { start: this.startDate, end: value.date };
        this.updateComesFromLastDP = true;
      }
      this.preventEndDayClick = false;
      this.isEditing = false;
      lastDP.hidePopover();
    } else {
      // Direct to-date change: user opened the "to" picker without clicking "from" first.
      // Commit immediately without a two-click confirmation dance.
      if (moment(this.startDate).isSameOrBefore(moment(value.date))) {
        this.preventEndInput = true;
        lastDP.updateValue({ start: this.startDate, end: value.date });
        this.dateRange = { start: this.startDate, end: value.date };
        lastDP.hidePopover();
      }
    }
  }

  private endInput(value: any) {
    const lastDP: any = this.$refs.lastDP;
    if (!value || !value.end) {
      return false;
    }
    if (this.preventEndInput) {
      this.preventEndInput = false;
      return;
    }
    // do nothing further if clicking in the first datepicker triggers an input event in the last
    if (this.updateComesFromFirstDP) {
      this.updateComesFromFirstDP = false;
      return;
    }
    this.updateComesFromLastDP = true;
    this.isEditing = false;
    this.startDate = this.tempDateRange.start;
    this.dateRange = {
      start: this.tempDateRange.start,
      end: this.tempDateRange.end,
    };
    lastDP.hidePopover();
    this.$nextTick(() => {
      this.updateComesFromLastDP = false;
    });
  }

  @Watch('firstOpen', { immediate: true })
  private onFirstOpenChange(value: boolean) {
    if (value) {
      this.lastOpen = false;
    }

    if (!value && !this.lastOpen) {
      this.isEditing = false;
    }
  }

  @Watch('lastOpen', { immediate: true })
  private onLastOpenChange(value: boolean) {
    if (value) {
      this.firstOpen = false;
    }

    if (!value && !this.firstOpen) {
      this.isEditing = false;
    }
  }

  @Watch('dateRange', { immediate: true })
  private onDateRangeChange(value: any, oldValue: any) {
    // only trigger a dateRange update if new value has a start and an end,
    // and end is same (one-day range) or after start
    if (
      value &&
      value.start &&
      value.end &&
      oldValue &&
      (value.start !== oldValue.start || value.end !== oldValue.end) &&
      moment(value.end).isSameOrAfter(value.start)
    ) {
      this.$emit('input', {
        start: moment(value.start).format('YYYY-MM-DD'),
        end: moment(value.end).format('YYYY-MM-DD'),
      });
      this.$gtm.trackEvent({
        event:
          this.gtmContext === 'modal'
            ? 'bulkUpdateCalendarUpdated'
            : 'availabilityPageCalendarUpdated',
      });
    }
  }

  @Watch('dashboard.dateRange', { immediate: true })
  private onDashboardDateRangeChange(value: any) {
    const lastDP: any = this.$refs.lastDP;
    this.preventFirstInput = true;
    this.preventEndInput = true;
    this.startDate = moment(value.start, 'YYYY-MM-DD').toDate();
    if (lastDP) {
      lastDP.updateValue({
        start: moment(value.start, 'YYYY-MM-DD').toDate(),
        end: moment(value.end, 'YYYY-MM-DD').toDate(),
      });
    }
    this.tempDateRange = {
      start: moment(value.start, 'YYYY-MM-DD').toDate(),
      end: moment(value.end, 'YYYY-MM-DD').toDate(),
    };
    this.dateRange.start = moment(value.start, 'YYYY-MM-DD').toDate();
    (this.dateRange.end = moment(value.end, 'YYYY-MM-DD').toDate()),
      this.$nextTick(() => {
        this.preventFirstInput = false;
        this.preventEndInput = false;
      });
  }

  @Watch('partner.lang', { immediate: true })
  private onLangChange() {
    this.localeId = moment.locale();
  }
}
